import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextHeader from 'components/CustomText/TextHeader';
import UploadDisplay from 'components/uploadFile/UploadDisplay';
import GeneralPostApprovalLayout from 'components/GeneralPostApprovalLayout';
import { CTC, CTI } from 'utility/postApprovalLookUp';
import { useDispatch, useSelector } from 'react-redux';
import { GET_CTCANDCTI_FILE, SET_POST_APPROVAL } from 'store/key';
import useQuery from 'hooks/useQuery';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { postApprovalPermissionChecking, toHalfWidthUnicode } from 'utility/general';
import CustomTextField from 'components/Input/CustomTextField';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import UploadFileInput from 'components/uploadFile/uploadFileInput';
import API from 'api/call';
import { setGlobalLoading } from 'store/global/reducer';
import { useHistory } from 'react-router';
import useDialog from 'components/ConfirmDialog/useDialog';
import { CTC_CTI_RE_SUBMITED } from 'utility/frontendMessage';

const useStyles = makeStyles((theme) => ({
    item: {
        marginTop: theme.spacing(2),
        lineHeight: 1.4,
    },
    link: {
        marginRight: theme.spacing(1),
    },
}));

const ViewCTCCTISubmissionDetail = () => {
    const location = useLocation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const showDialog = useDialog();
    const history = useHistory();
    const reportId = useQuery('reportId');
    const documentType = location.pathname === '/view-ctc' ? CTC : CTI;

    const isAmendmentForm = location?.pathname?.includes('application-amendment-detail');

    const ctcAndCtiSubmission = useSelector((state) => state.postApproval.ctcAndCtiSubmission);

    const status = ctcAndCtiSubmission.lastStatus;

    const hasUploaded = ctcAndCtiSubmission.uploadDocument;

    const isPermission = postApprovalPermissionChecking({ status, documentType, reportData: ctcAndCtiSubmission });

    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);
    const [expiryDtmValid, setExpiryDtmValid] = useState(true);
    const [noValid, setNoValid] = useState(true);

    const [loadingFile, setLoadingFile] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const setData = (key, value) => {
        dispatch({
            type: SET_POST_APPROVAL,
            payload: {
                ctcAndCtiSubmission: {
                    ...ctcAndCtiSubmission,
                    [key]: value,
                },
            },
        });
    };

    const getDetail = () => {
        setIsLoading(true);
        dispatch({
            type: GET_CTCANDCTI_FILE,
            payload: {
                docType: documentType,
                reportId,
                callback: () => setIsLoading(false),
                onPermissionError: () => {
                    history?.push('/');
                },
            },
        });
    };

    useEffect(() => {
        getDetail();
        // eslint-disable-next-line
    }, [reportId, documentType]);

    const onRemoveFile = () => {
        dispatch({
            type: SET_POST_APPROVAL,
            payload: {
                ctcAndCtiSubmission: {
                    ...ctcAndCtiSubmission,
                    uploadDocument: '',
                    uploadFileId: '',
                },
            },
        });
    };

    const reSubmitSuccess = (type) => {
        showDialog({
            description: type + CTC_CTI_RE_SUBMITED,
            disableCancelButton: true,
        });
        history.push('/');
    };

    const reSubmitCTCAndCTI = () => {
        if (expiryDtmValid && noValid && hasUploaded) {
            setGlobalLoading(true);
            if (documentType === CTC) {
                let data = {
                    expiryDtm: new Date(ctcAndCtiSubmission.expiryDtm),
                    certNo: ctcAndCtiSubmission.certNo,
                    appId: ctcAndCtiSubmission.appId,
                    reportId,
                };
                if (ctcAndCtiSubmission.uploadFileId) {
                    data.uploadFileId = ctcAndCtiSubmission.uploadFileId;
                }

                API.approval
                    .SubmitCtc(data)
                    .then(() => {
                        reSubmitSuccess('CTC');
                    })
                    .finally(() => setGlobalLoading(false));
            } else {
                let data = {
                    expiryDtm: new Date(ctcAndCtiSubmission.expiryDtm),
                    policyNo: ctcAndCtiSubmission.policyNo,
                    appId: ctcAndCtiSubmission.appId,
                    reportId,
                };
                if (ctcAndCtiSubmission.uploadFileId) {
                    data.uploadFileId = ctcAndCtiSubmission.uploadFileId;
                }

                API.approval
                    .SubmitCti(data)
                    .then(() => {
                        reSubmitSuccess('CTI');
                    })
                    .finally(() => setGlobalLoading(false));
            }
        } else {
            setForceErrorDisplay(true);
        }
    };
    const fileOnChange = (list) => {
        if (list.length) {
            setLoadingFile(true);
            let requestFormData = new FormData();

            let file = list[0];

            try {
                // convert the file name to all half width unicode for fix of code scan issue
                file = new File([list[0]], toHalfWidthUnicode(list[0].name), {
                    type: list[0].type,
                });
            } catch (error) {
                console.log('%c 121123 ViewCTCCTISubmissionDetail.jsx error', 'background: green', error);
            }

            requestFormData.append('file', file);

            API.file
                .uploadDocument(requestFormData)
                .then((res) => {
                    const { key, fileName } = res.data?.result?.result[0];
                    dispatch({
                        type: SET_POST_APPROVAL,
                        payload: {
                            ctcAndCtiSubmission: {
                                ...ctcAndCtiSubmission,
                                uploadDocument: fileName,
                                tempFile: list[0],
                                uploadFileId: key,
                            },
                        },
                    });
                })
                .finally(() => {
                    setLoadingFile(false);
                });
        }
    };

    const checkIsFormDataChanged = () => {
        if (ctcAndCtiSubmission.uploadFileId !== undefined) {
            return true;
        }
        if (
            new Date(ctcAndCtiSubmission?.oldData?.expiryDtm).getTime() !==
            new Date(ctcAndCtiSubmission?.expiryDtm).getTime()
        ) {
            return true;
        }
        if (ctcAndCtiSubmission.oldData.certNo !== ctcAndCtiSubmission.certNo) {
            return true;
        }
        if (ctcAndCtiSubmission.oldData.policyNo !== ctcAndCtiSubmission.policyNo) {
            return true;
        }

        return false;
    };

    return (
        <GeneralPostApprovalLayout
            isLoading={isLoading}
            type={documentType}
            status={status}
            isViewOnly={!isPermission}
            reportId={reportId}
            isPermission={isPermission}
            reSubmitCTCAndCTI={reSubmitCTCAndCTI}
            hasCCOAction={ctcAndCtiSubmission.hasCCOAction}
            checkIsFormDataChanged={checkIsFormDataChanged}
        >
            <TextHeader style={{ textAlign: 'center', marginBottom: 20 }}>
                View Certificate for Clinical Trial / Medicinal Test (CTC)/ Clinical Trial Insurance (CTI) Report Form
            </TextHeader>
            <Grid>
                {ctcAndCtiSubmission && (
                    <Grid container style={{ marginBottom: 36 }}>
                        <TextHeader>
                            {documentType === CTC
                                ? 'Certificate for Clinical Trial / Medicinal Test (CTC)'
                                : 'Clinical Trial Insurance (CTI)'}
                        </TextHeader>
                        <Grid container className={classes.item}>
                            <Grid container wrap="nowrap" alignItems="center">
                                <Grid container style={{ width: 160, wordBreak: 'normal' }}>
                                    New Expiry Date:
                                </Grid>
                                {isPermission ? (
                                    <Grid container item xs={3}>
                                        <CustomDatePicker
                                            name="expiryDtm"
                                            value={ctcAndCtiSubmission.expiryDtm}
                                            label="New Expiry Date"
                                            handleChangeDate={(e) => setData('expiryDtm', e.target.value)}
                                            validators={['isRequired']}
                                            forceErrorDisplay={forceErrorDisplay}
                                            isValidValue={(isValid) => setExpiryDtmValid(isValid)}
                                        />
                                    </Grid>
                                ) : (
                                    ctcAndCtiSubmission.expiryDtm
                                )}
                            </Grid>
                            <Grid container className={classes.item} wrap="nowrap" alignItems="center">
                                <Grid container style={{ width: 160 }}>
                                    {documentType === CTC ? 'Certificate No' : 'Policy No'}:
                                </Grid>
                                {isPermission ? (
                                    <Grid container item xs={3}>
                                        <CustomTextField
                                            label={documentType === CTC ? 'Certificate No' : 'Policy No'}
                                            name={documentType === CTC ? 'certNo' : 'policyNo'}
                                            value={
                                                documentType === CTC
                                                    ? ctcAndCtiSubmission.certNo
                                                    : ctcAndCtiSubmission.policyNo
                                            }
                                            type="input"
                                            onChange={(e) =>
                                                setData(documentType === CTC ? 'certNo' : 'policyNo', e.target.value)
                                            }
                                            validators={['isRequired']}
                                            forceErrorDisplay={forceErrorDisplay}
                                            isValidValue={(isValid) => setNoValid(isValid)}
                                            isNoSpace
                                            maxLength={255}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid container item xs={10} style={{ wordBreak: 'break-word' }}>
                                        {documentType === CTC
                                            ? ctcAndCtiSubmission.certNo
                                            : ctcAndCtiSubmission.policyNo}
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container item xs={12} wrap="nowrap" alignItems="center" className={classes.item}>
                                <Grid container style={{ width: 160 }}>
                                    Upload Document:{' '}
                                </Grid>
                                <Grid container item xs={3}>
                                    {isPermission && !hasUploaded ? (
                                        <Grid container wrap="nowrap" alignItems="center">
                                            <div style={{ width: 85 }}>
                                                <UploadFileInput isLoading={loadingFile} fileOnChange={fileOnChange} />
                                            </div>
                                            {forceErrorDisplay && (
                                                <Grid style={{ color: 'red' }}>No file has uploaded!</Grid>
                                            )}
                                            {isAmendmentForm && (
                                                <Typography
                                                    color="textSecondary"
                                                    style={{ marginTop: 4, color: '#FF0000' }}
                                                >
                                                    * Clean and track version of documents are required to upload
                                                    (except newly-submitted document) for Amendment Application
                                                </Typography>
                                            )}
                                        </Grid>
                                    ) : (
                                        <UploadDisplay
                                            fileList={
                                                hasUploaded
                                                    ? [
                                                          {
                                                              name: ctcAndCtiSubmission.uploadDocument,
                                                              downloadId: ctcAndCtiSubmission.reportId,
                                                              file: ctcAndCtiSubmission.tempFile,
                                                          },
                                                      ]
                                                    : []
                                            }
                                            enableDownload={true}
                                            downloadType={documentType === CTC ? 'ctc' : 'cti'}
                                            enableRemove={isPermission}
                                            onRemove={onRemoveFile}
                                            isLocalDownload={ctcAndCtiSubmission.tempFile !== undefined}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </GeneralPostApprovalLayout>
    );
};

export default ViewCTCCTISubmissionDetail;
